import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Avatar,
  Backdrop,
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SettingsIcon from "@mui/icons-material/Settings";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import InventoryIcon from "@mui/icons-material/Inventory";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import LogoutIcon from "@mui/icons-material/Logout";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { AuthContext } from "../../context/auth-context";
import { signOutUser } from "../../firebase/firebaseAuth";
import { ReactComponent as FullLogoSVG } from "../../static/svg/full-logo.svg";
import { ReactComponent as StackedLogoSVG } from "../../static/svg/logo.svg";

const SIDEBAR_EXPANDED = 240;
const SIDEBAR_COLLAPSED = 68;
const IDLE_MS = 30 * 60 * 1000; // 30 minutes

const NAV_ITEMS = [
  { label: "Overview", path: "/dashboard", icon: <DashboardIcon /> },
  { label: "Orders", path: "/dashboard/orders", icon: <ShoppingBagIcon /> },
  {
    label: "Bookings",
    path: "/dashboard/bookings",
    icon: <CalendarMonthIcon />,
  },
  { label: "Settings", path: "/dashboard/settings", icon: <SettingsIcon /> },
];

const ADMIN_ITEMS = [
  {
    label: "Schedule",
    path: "/dashboard/admin/schedule",
    icon: <EventNoteIcon />,
  },
  {
    label: "Availability",
    path: "/dashboard/admin/availability",
    icon: <EventBusyIcon />,
  },
  {
    label: "Services",
    path: "/dashboard/admin/services",
    icon: <ContentCutIcon />,
  },
  {
    label: "Add Product",
    path: "/dashboard/admin/create",
    icon: <AddCircleOutlineIcon />,
  },
  {
    label: "All Products",
    path: "/dashboard/admin/products",
    icon: <InventoryIcon />,
  },
];

const COUNTDOWN_SECS = 10;

function SessionTimeoutModal({ open, countdown, onContinue, onLogout }) {
  const progress = (countdown / COUNTDOWN_SECS) * 100;
  return (
    <Backdrop
      open={open}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 20,
        backdropFilter: "blur(2px)",
      }}
    >
      <Paper
        elevation={6}
        sx={{
          width: { xs: "92vw", sm: 400 },
          borderRadius: "16px",
          p: 0,
          outline: "none",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            borderBottom: "1px solid #e5e7eb",
            borderRadius: "16px 16px 0 0",
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
            Session Expiring
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.25 }}>
            You've been inactive for 30 minutes
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ p: 3 }}>
          {/* Countdown ring */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box sx={{ position: "relative", display: "inline-flex", mb: 1.5 }}>
              <CircularProgress
                variant="determinate"
                value={100}
                size={80}
                thickness={4}
                sx={{ color: "#e5e7eb", position: "absolute" }}
              />
              <CircularProgress
                variant="determinate"
                value={progress}
                size={80}
                thickness={4}
                sx={{
                  color: countdown <= 3 ? "#ef4444" : "#194049",
                  transition: "color 0.3s",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: countdown <= 3 ? "#ef4444" : "#194049",
                    transition: "color 0.3s",
                  }}
                >
                  {countdown}
                </Typography>
              </Box>
            </Box>
            <Typography
              sx={{ fontSize: 13, color: "#374151", textAlign: "center" }}
            >
              You will be automatically logged out in{" "}
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  color: countdown <= 3 ? "#ef4444" : "#111827",
                }}
              >
                {countdown} second{countdown !== 1 ? "s" : ""}
              </Box>
            </Typography>
          </Box>

          {/* Info box */}
          <Box
            sx={{
              bgcolor: "#fff7ed",
              border: "1px solid #fed7aa",
              borderRadius: "8px",
              px: 2,
              py: 1.5,
              mb: 3,
            }}
          >
            <Typography sx={{ fontSize: 12, color: "#92400e" }}>
              Any unsaved changes will be lost if you're logged out.
            </Typography>
          </Box>

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Box
              onClick={onLogout}
              sx={{
                flex: 1,
                py: 1.25,
                fontSize: 13,
                fontWeight: 600,
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                cursor: "pointer",
                color: "#6b7280",
                bgcolor: "#fff",
                textAlign: "center",
                "&:hover": { color: "#ef4444", borderColor: "#fecaca" },
                transition: "all 0.15s",
              }}
            >
              Logout
            </Box>
            <Box
              onClick={onContinue}
              sx={{
                flex: 2,
                py: 1.25,
                fontSize: 13,
                fontWeight: 600,
                borderRadius: "8px",
                cursor: "pointer",
                color: "#fff",
                bgcolor: "#194049",
                textAlign: "center",
                "&:hover": { bgcolor: "#0f2a31" },
                transition: "all 0.15s",
              }}
            >
              Continue Session
            </Box>
          </Box>
        </Box>
      </Paper>
    </Backdrop>
  );
}

function SidebarContent({ expanded, onClose, isMobile }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logoutUser } = useContext(AuthContext);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.role === "admin";

  const isActive = (path) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    // keep "All Products" active while editing a product
    if (
      path === "/dashboard/admin/products" &&
      location.pathname.startsWith("/dashboard/admin/update")
    )
      return true;
    return location.pathname.startsWith(path);
  };

  const handleNav = (path) => {
    navigate(path);
    if (isMobile && onClose) onClose();
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
      logoutUser();
      navigate("/");
    } catch (e) {
      console.error(e);
    }
  };

  const itemSx = (active) => ({
    borderRadius: "8px",
    mx: 1,
    mb: 0.5,
    backgroundColor: active ? "#194049" : "transparent",
    color: active ? "#fff" : "#4b5563",
    "&:hover": {
      backgroundColor: active ? "#194049" : "#f3f4f6",
    },
    minHeight: 44,
    justifyContent: expanded ? "initial" : "center",
    px: expanded ? 2 : 1.5,
  });

  const iconSx = (active) => ({
    color: active ? "#fff" : "#6b7280",
    minWidth: 0,
    mr: expanded ? 1.5 : 0,
    justifyContent: "center",
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#fff",
        borderRight: "1px solid #e5e7eb",
        overflow: "hidden",
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: expanded ? "flex-start" : "center",
          px: expanded ? 2 : 1,
          flexShrink: 0,
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        {expanded ? (
          <FullLogoSVG width="130px" height="40px" />
        ) : (
          <StackedLogoSVG width="36px" height="36px" />
        )}
      </Box>

      {/* Nav items */}
      <Box sx={{ flex: 1, overflowY: "auto", pt: 1.5 }}>
        <List disablePadding>
          {NAV_ITEMS.map((item) => {
            const active = isActive(item.path);
            return (
              <Tooltip
                key={item.path}
                title={!expanded ? item.label : ""}
                placement="right"
              >
                <ListItem disablePadding>
                  <ListItemButton
                    sx={itemSx(active)}
                    onClick={() => handleNav(item.path)}
                  >
                    <ListItemIcon sx={iconSx(active)}>{item.icon}</ListItemIcon>
                    {expanded && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: 14,
                          fontWeight: active ? 600 : 400,
                          color: active ? "#fff" : "#4b5563",
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            );
          })}

          {isAdmin && (
            <>
              <Divider sx={{ my: 1, mx: 2 }} />
              {expanded && (
                <Typography
                  sx={{
                    px: 3,
                    py: 0.5,
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#9ca3af",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  Admin
                </Typography>
              )}
              {ADMIN_ITEMS.map((item) => {
                const active = isActive(item.path);
                return (
                  <Tooltip
                    key={item.path}
                    title={!expanded ? item.label : ""}
                    placement="right"
                  >
                    <ListItem disablePadding>
                      <ListItemButton
                        sx={itemSx(active)}
                        onClick={() => handleNav(item.path)}
                      >
                        <ListItemIcon sx={iconSx(active)}>
                          {item.icon}
                        </ListItemIcon>
                        {expanded && (
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{
                              fontSize: 14,
                              fontWeight: active ? 600 : 400,
                              color: active ? "#fff" : "#4b5563",
                            }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  </Tooltip>
                );
              })}
            </>
          )}
        </List>
      </Box>

      {/* Visit Store */}
      <Box sx={{ px: 1, pb: 1 }}>
        <Tooltip title={!expanded ? "Visit Store" : ""} placement="right">
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/"
              sx={{
                borderRadius: "8px",
                mx: 0,
                color: "#6b7280",
                border: "1px solid #e5e7eb",
                "&:hover": {
                  bgcolor: "#f3f4f6",
                  color: "#194049",
                  borderColor: "#194049",
                },
                minHeight: 40,
                justifyContent: expanded ? "initial" : "center",
                px: expanded ? 2 : 1.5,
                transition: "all 0.15s",
              }}
            >
              <ListItemIcon
                sx={{
                  color: "inherit",
                  minWidth: 0,
                  mr: expanded ? 1.5 : 0,
                  justifyContent: "center",
                }}
              >
                <StorefrontOutlinedIcon sx={{ fontSize: 20 }} />
              </ListItemIcon>
              {expanded && (
                <ListItemText
                  primary="Visit Store"
                  primaryTypographyProps={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "inherit",
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        </Tooltip>
      </Box>

      {/* User info + Sign out */}
      <Box sx={{ flexShrink: 0, borderTop: "1px solid #e5e7eb", pb: 1, pt: 1 }}>
        {expanded && (
          <Box
            sx={{
              px: 2,
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Avatar
              sx={{ width: 34, height: 34, bgcolor: "#194049", fontSize: 14 }}
            >
              {user?.firstname?.[0]?.toUpperCase() || "U"}
            </Avatar>
            <Box sx={{ overflow: "hidden" }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#111827",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user?.firstname} {user?.lastname}
              </Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  color: "#6b7280",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user?.email}
              </Typography>
            </Box>
          </Box>
        )}
        <Tooltip title={!expanded ? "Sign out" : ""} placement="right">
          <ListItem disablePadding>
            <ListItemButton
              sx={{
                ...itemSx(false),
                color: "#ef4444",
                "&:hover": { backgroundColor: "#fef2f2" },
              }}
              onClick={handleSignOut}
            >
              <ListItemIcon
                sx={{
                  color: "#ef4444",
                  minWidth: 0,
                  mr: expanded ? 1.5 : 0,
                  justifyContent: "center",
                }}
              >
                <LogoutIcon />
              </ListItemIcon>
              {expanded && (
                <ListItemText
                  primary="Sign Out"
                  primaryTypographyProps={{ fontSize: 14, fontWeight: 400 }}
                />
              )}
            </ListItemButton>
          </ListItem>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function DashboardLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showIdleModal, setShowIdleModal] = useState(false);
  const [idleCountdown, setIdleCountdown] = useState(COUNTDOWN_SECS);
  const navigate = useNavigate();
  const { logoutUser } = useContext(AuthContext);
  const idleTimer = useRef(null);
  const countdownTimer = useRef(null);
  const modalActive = useRef(false);
  const location = useLocation();

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const pageTitles = {
    "/dashboard": "Overview",
    "/dashboard/orders": "Orders",
    "/dashboard/bookings": "Bookings",
    "/dashboard/settings": "Settings",
    "/dashboard/admin/schedule": "Booking Schedule",
    "/dashboard/admin/availability": "Availability",
    "/dashboard/admin/services": "Services",
    "/dashboard/admin/create": "Add Product",
    "/dashboard/admin/products": "All Products",
  };

  const pageTitle =
    pageTitles[location.pathname] ||
    (location.pathname.startsWith("/dashboard/admin/update")
      ? "Edit Product"
      : "Dashboard");

  const handleSignOut = useCallback(async () => {
    try {
      await signOutUser();
      logoutUser();
      navigate("/");
    } catch (e) {
      console.error(e);
    }
  }, [logoutUser, navigate]);

  const startCountdown = useCallback(() => {
    modalActive.current = true;
    setShowIdleModal(true);
    setIdleCountdown(COUNTDOWN_SECS);
    countdownTimer.current = setInterval(() => {
      setIdleCountdown((n) => {
        if (n <= 1) {
          clearInterval(countdownTimer.current);
          handleSignOut();
          return 0;
        }
        return n - 1;
      });
    }, 1000);
  }, [handleSignOut]);

  const resetIdleTimer = useCallback(() => {
    if (modalActive.current) return;
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(startCountdown, IDLE_MS);
  }, [startCountdown]);

  const handleContinueSession = useCallback(() => {
    modalActive.current = false;
    clearInterval(countdownTimer.current);
    setShowIdleModal(false);
    setIdleCountdown(COUNTDOWN_SECS);
    resetIdleTimer();
  }, [resetIdleTimer]);

  const handleIdleLogout = useCallback(() => {
    clearInterval(countdownTimer.current);
    setShowIdleModal(false);
    handleSignOut();
  }, [handleSignOut]);

  // Start idle watcher
  useEffect(() => {
    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "touchstart",
      "scroll",
    ];
    events.forEach((e) =>
      window.addEventListener(e, resetIdleTimer, { passive: true }),
    );
    resetIdleTimer();
    return () => {
      clearTimeout(idleTimer.current);
      clearInterval(countdownTimer.current);
      events.forEach((e) => window.removeEventListener(e, resetIdleTimer));
    };
  }, [resetIdleTimer]);

  const sidebarWidth = sidebarOpen ? SIDEBAR_EXPANDED : SIDEBAR_COLLAPSED;

  return (
    <Box sx={{ display: "flex", height: "100vh", bgcolor: "#f5f7fa" }}>
      <SessionTimeoutModal
        open={showIdleModal}
        countdown={idleCountdown}
        onContinue={handleContinueSession}
        onLogout={handleIdleLogout}
      />
      {/* Desktop sidebar */}
      {!isMobile && (
        <Box
          sx={{
            width: sidebarWidth,
            flexShrink: 0,
            transition: "width 0.2s ease",
            overflow: "hidden",
            height: "100vh",
            position: "fixed",
            left: 0,
            top: 0,
            zIndex: 1200,
          }}
        >
          <SidebarContent expanded={sidebarOpen} />
        </Box>
      )}

      {/* Mobile drawer */}
      {isMobile && (
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: SIDEBAR_EXPANDED,
              boxSizing: "border-box",
            },
          }}
        >
          <SidebarContent
            expanded
            isMobile
            onClose={() => setMobileOpen(false)}
          />
        </Drawer>
      )}

      {/* Main content */}
      <Box
        sx={{
          flex: 1,
          ml: isMobile ? 0 : `${sidebarWidth}px`,
          transition: "margin-left 0.2s ease",
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          overflow: "hidden",
        }}
      >
        {/* Top bar */}
        <Box
          sx={{
            height: 64,
            bgcolor: "#fff",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: { xs: 2, md: 3 },
            flexShrink: 0,
            position: "sticky",
            top: 0,
            zIndex: 1100,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton
              onClick={() =>
                isMobile ? setMobileOpen(true) : setSidebarOpen((p) => !p)
              }
              size="small"
              sx={{ color: "#374151" }}
            >
              {!isMobile && sidebarOpen ? <ChevronLeftIcon /> : <MenuIcon />}
            </IconButton>
            <Typography
              sx={{
                fontSize: { xs: 16, md: 18 },
                fontWeight: 600,
                color: "#111827",
              }}
            >
              {pageTitle}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: "#194049",
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              {user?.firstname?.[0]?.toUpperCase() || "U"}
            </Avatar>
            {!isMobile && (
              <Typography
                sx={{ fontSize: 13, color: "#374151", fontWeight: 500 }}
              >
                {user?.firstname} {user?.lastname}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Page content */}
        <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 2, md: 3 } }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
